import { AlertTriangle } from 'lucide-react';
import type {
  BookingCancellationPreviewResponse,
  BookingCancellationRefundPolicy,
} from '@/shared/api';
import { formatMoney } from '@/shared/lib/format';

export function CancellationAmounts({ preview }: { preview: BookingCancellationPreviewResponse }) {
  return (
    <div className="bg-card-alt border-line grid grid-cols-3 gap-3 rounded-md border p-4 text-sm">
      <Amount label="Bezahlt" minor={preview.paidAmountMinor} currency={preview.currency} />
      <Amount label="Erstattet" minor={preview.refundedAmountMinor} currency={preview.currency} />
      <Amount
        label="Offener Rest"
        minor={preview.remainingRefundableAmountMinor}
        currency={preview.currency}
      />
    </div>
  );
}

export function PaidAdminOnlyHint() {
  return (
    <p className="border-sealwax/30 bg-sealwax/5 text-sealwax flex gap-2 rounded-md border p-3 text-sm">
      <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
      Eine bereits bezahlte Buchung darf nur ein Admin stornieren.
    </p>
  );
}

interface RefundDecisionProps {
  decision: BookingCancellationRefundPolicy | undefined;
  onDecision: (decision: BookingCancellationRefundPolicy) => void;
}

export function RefundDecision({ decision, onDecision }: RefundDecisionProps) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-ink text-sm font-semibold">Zahlung behandeln</legend>
      <DecisionOption
        checked={decision === 'FullRemaining'}
        onChange={() => onDecision('FullRemaining')}
        label="Restbetrag vollständig erstatten"
      />
      <DecisionOption
        checked={decision === 'None'}
        onChange={() => onDecision('None')}
        label="Zahlung einbehalten"
      />
      {decision === 'None' && (
        <p className="text-ink-faint pt-1 text-xs">
          Das Einbehalten einer Zahlung ist noch nicht implementiert. Version 1 erstattet manuell
          stornierte Buchungen immer vollständig.
        </p>
      )}
    </fieldset>
  );
}

function Amount({ label, minor, currency }: { label: string; minor: number; currency: string }) {
  return (
    <div>
      <p className="text-ink-faint text-xs">{label}</p>
      <p className="text-ink mt-1 font-semibold">{formatMoney(minor / 100, currency)}</p>
    </div>
  );
}

function DecisionOption({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className="border-line hover:bg-card-alt flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm">
      <input type="radio" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}
